import { ImageFit as FluentImageFit } from "@fluentui/react";
import { BaseProps } from "../../shared/props";
import { IconProps } from "../Icon/Icon";

export type ImageFit = "none" | "center" | "center-contain" | "center-cover" | "contain" | "cover";
export type BorderRadius = "50%" | "4px" | "8px" | "16px";
export type ImageGradientType = "toLeft" | "toRight" | "toTop" | "toBottom";

export const toFluentImageFit: { [K in ImageFit]: FluentImageFit } = {
    "none": FluentImageFit.none,
    "center": FluentImageFit.center,
    "center-contain": FluentImageFit.centerContain,
    "center-cover": FluentImageFit.centerCover,
    "contain": FluentImageFit.contain,
    "cover": FluentImageFit.cover
};

export interface BaseImageProps extends BaseProps {
    /**
     * Image URL
     */
    readonly src: string;

    /**
     * Used to determine how the image is scaled and cropped to fit the frame.
     *
     * If both width and height are provided, this property will have no effect,
     * as the image will be scaled to that size. This may result in a distorted image.
     *
     * See https://developer.microsoft.com/en-us/fluentui#/controls/web/image for
     * more information on how this propery works.
     */
    readonly imageFit?: ImageFit;

    /**
     * Image width value
     */
    readonly width?: number | string;

    /**
     * Image height value
     */
    readonly height?: number | string;

    /**
     * If true, fades the image in when loaded. Defaults to true.
     */
    readonly shouldFadeIn?: boolean;

    /**
     * If true, the image frame will expand to fill its parent container.
     * No visible change since it doesn't control the image but the frame of it.
     * To control the image, use imageFit prop.
     */
    readonly maximizeFrame?: boolean;

    /**
     * Alternative src in case the provided one fails to load.
     */
    readonly errorSrc?: string;

    /**
     * Icon to show in case of error (if errorSrc not specified and default not wanted)
     */
    readonly fallbackIconProps?: Pick<IconProps, "iconName" | "color">;

    /**
     * Whether to have a border radius around the image
     */
    readonly borderRadius?: BorderRadius;

    /**
     * Whether to add grayscale filter to image
     */
    readonly grayscale?: boolean;

    /**
     * Whether to make image appear as disabled
     */
    readonly disabled?: boolean;

    /**
     * Whether to add filter to the image in high contrast mode
     */
    readonly enableHighContrastMode?: boolean;

    /**
     * Custom image gradient style
     */
    readonly imageGradientType?: ImageGradientType;
}

// Prop interface for a visible image, which needs alt text
export interface AltImageProps extends BaseImageProps {
    /**
     * discriminant for the Discriminated Union
     */
    readonly imageType?: "altTextImage";

    /**
     * Specifies alternative information for an image if a user for some reason cannot view it
     * (because of slow connection, an error in the src attribute).
     */
    readonly alt: string;
}

// Prop interface for a hidden image
interface AriaHiddenImageProps extends BaseImageProps {
    /**
     * discriminant for the Discriminated Union
     */
    readonly imageType: "ariaHiddenImage";
}

export type ImageProps = AltImageProps | AriaHiddenImageProps;
